import { apiClient, stagingApiClient } from './client';
import type {
  WeeklyReportConfig,
  WeeklyReportResult,
  Tenant,
  TenantStats,
  ProductIndexStatus,
  Product,
  ChatTestBatch,
  ChatTestResult,
  EvaluatedChatTestBatch,
  SectorConfig,
  AppFeatures,
  FeedbackAlertPreview,
  TenantChatConfig,
  AvailableModel,
  RequestLog,
  ErrorLog,
  ConversationLog,
  ChatMessageLog,
  FeedbackLog,
  UserReport,
  UserReportStats,
  UserReportStatus,
  UserReportType,
  DailyStats,
  StatsSummary,
  PaginatedResponse,
  ChatbotUser,
  CreateChatbotUserBody,
  UpdateChatbotUserBody,
  BackofficeUser,
  CreateBackofficeUserBody,
  UpdateBackofficeUserBody,
  TenantOperator,
  UpsertTenantOperatorBody,
  UpdateTenantOperatorBody,
  BackofficeRole,
  SystemHealth,
  SystemMetrics,
  UnusedResources,
  AlertSettings,
  AlertCheckResult,
  MonitoredDomain,
  DomainCheckResult,
  BackupOverviewItem,
  BackupConfig,
  BackupRun,
  BackupFile,
  BackupLastRun,
  StagingOverview,
  StagingSyncRun,
  StagingSyncRequest,
} from '../models';

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return entries.length ? `?${entries.join('&')}` : '';
}

class ApiService {
  private client = apiClient;
  private stagingClient = stagingApiClient;

  // ── Tenants ──

  async listTenants(): Promise<Tenant[]> {
    const response = (await this.client.get('/admin/tenants')) as { data: unknown[] };
    const data = (response.data ?? []) as Record<string, unknown>[];
    const seen = new Set<string>();
    return data
      .map((e) => ({
        tenantId: String(e.tenantId ?? ''),
        name: String(e.name ?? ''),
        dbName: String(e.dbName ?? ''),
        createdAt: String(e.createdAt ?? new Date().toISOString()),
      }) as Tenant)
      .filter((t) => {
        const key = t.name.trim();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  async getTenant(tenantId: string): Promise<Tenant> {
    const response = (await this.client.get(`/admin/tenants/${tenantId}`)) as {
      data: Record<string, unknown>;
    };
    const data = response.data;
    return {
      tenantId: String(data.tenantId ?? ''),
      name: String(data.name ?? ''),
      dbName: String(data.dbName ?? ''),
      createdAt: String(data.createdAt ?? new Date().toISOString()),
    };
  }

  async createTenant(body: { name: string; dbName: string }): Promise<Tenant> {
    const response = (await this.client.post('/admin/tenants', body)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    return {
      tenantId: String(data.tenantId ?? ''),
      name: String(data.name ?? ''),
      dbName: String(data.dbName ?? ''),
      createdAt: String(data.createdAt ?? new Date().toISOString()),
    };
  }

  async deleteTenant(tenantId: string): Promise<void> {
    await this.client.delete(`/admin/tenants/${tenantId}`);
  }

  async getTenantChatConfig(tenantId: string): Promise<TenantChatConfig> {
    const response = (await this.client.get(`/admin/tenants/${tenantId}/config`)) as {
      data: Record<string, unknown> | null;
    };
    const data = response.data ?? {};
    return {
      chatModel: String(data.chatModel ?? ''),
      routerModel: String(data.routerModel ?? ''),
      reasoningEnabled: Boolean(data.reasoningEnabled ?? false),
    };
  }

  async updateTenantChatConfig(
    tenantId: string,
    body: { chatModel: string; routerModel: string; reasoningEnabled: boolean },
  ): Promise<TenantChatConfig> {
    const response = (await this.client.put(`/admin/tenants/${tenantId}/config`, body)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    return {
      chatModel: String(data.chatModel ?? ''),
      routerModel: String(data.routerModel ?? ''),
      reasoningEnabled: Boolean(data.reasoningEnabled ?? false),
    };
  }

  async listAvailableModels(): Promise<{ models: AvailableModel[]; defaultModel: string }> {
    const response = (await this.client.get('/admin/llm-models')) as {
      models: Record<string, unknown>[];
      defaultModel?: string;
    };
    return {
      models: (response.models ?? []).map((m) => ({
        id: String(m.id ?? ''),
        name: String(m.name ?? m.id ?? ''),
        provider: String(m.provider ?? ''),
        promptPricePerM: typeof m.promptPricePerM === 'number' ? m.promptPricePerM : null,
        completionPricePerM: typeof m.completionPricePerM === 'number' ? m.completionPricePerM : null,
      })),
      defaultModel: String(response.defaultModel ?? ''),
    };
  }

  // ── Chatbot Users ──

  private parseChatbotUser(data: Record<string, unknown>): ChatbotUser {
    return {
      id: String(data.id ?? data._id ?? ''),
      firebaseId: String(data.firebaseId ?? ''),
      firstName: String(data.firstName ?? ''),
      lastName: String(data.lastName ?? ''),
      email: String(data.email ?? ''),
      disabled: Boolean(data.disabled ?? false),
      tenantIds: Array.isArray(data.tenantIds)
        ? (data.tenantIds as unknown[]).filter((v): v is string => typeof v === 'string')
        : [],
      isSuperAdmin: Boolean(data.isSuperAdmin ?? false),
      mustChangePassword: Boolean(data.mustChangePassword ?? false),
      passwordChangedAt: data.passwordChangedAt ? String(data.passwordChangedAt) : null,
      lastSignInAt: data.lastSignInAt ? String(data.lastSignInAt) : null,
      emailVerified: Boolean(data.emailVerified ?? false),
      firebaseCreatedAt: data.firebaseCreatedAt ? String(data.firebaseCreatedAt) : null,
      createdAt: String(data.createdAt ?? new Date().toISOString()),
      updatedAt: String(data.updatedAt ?? new Date().toISOString()),
    };
  }

  async listUsers(): Promise<ChatbotUser[]> {
    const response = (await this.client.get('/admin/users')) as {
      users?: Record<string, unknown>[];
    };
    const users = Array.isArray(response.users) ? response.users : [];
    return users.map((u) => this.parseChatbotUser(u));
  }

  async createUser(body: CreateChatbotUserBody): Promise<ChatbotUser> {
    const response = (await this.client.post('/admin/users', body as unknown as Record<string, unknown>)) as {
      user?: Record<string, unknown>;
    };
    const user = response.user ?? {};
    return this.parseChatbotUser(user);
  }

  async updateUser(firebaseId: string, body: UpdateChatbotUserBody): Promise<ChatbotUser> {
    const response = (await this.client.put(`/admin/users/${firebaseId}`, body as unknown as Record<string, unknown>)) as {
      user?: Record<string, unknown>;
    };
    const user = response.user ?? {};
    return this.parseChatbotUser(user);
  }

  async disableUser(firebaseId: string): Promise<void> {
    await this.client.post(`/admin/users/${firebaseId}/disable`);
  }

  async enableUser(firebaseId: string): Promise<void> {
    await this.client.post(`/admin/users/${firebaseId}/enable`);
  }

  async deleteUser(firebaseId: string): Promise<void> {
    await this.client.delete(`/admin/users/${firebaseId}`);
  }

  async resetUserPassword(
    email: string,
  ): Promise<{ message: string; link?: string; emailSent: boolean }> {
    const response = (await this.client.post('/admin/users/reset-password', { email })) as {
      message?: string;
      link?: string;
      emailSent?: boolean;
    };
    return {
      message: String(response.message ?? 'Password reset email sent'),
      link: response.link ? String(response.link) : undefined,
      emailSent: Boolean(response.emailSent ?? false),
    };
  }

  async regenerateUserPassword(
    firebaseId: string,
  ): Promise<{ message: string; email: string; password: string; emailSent: boolean }> {
    const response = (await this.client.post(
      `/admin/users/${firebaseId}/regenerate-password`,
    )) as {
      message?: string;
      email?: string;
      password?: string;
      emailSent?: boolean;
    };
    return {
      message: String(response.message ?? 'Nuove credenziali generate'),
      email: String(response.email ?? ''),
      password: String(response.password ?? ''),
      emailSent: Boolean(response.emailSent ?? false),
    };
  }

  // ── Backoffice Users ──

  private parseBackofficeUser(data: Record<string, unknown>): BackofficeUser {
    const role = String(data.role ?? 'admin');
    return {
      id: String(data.id ?? data._id ?? ''),
      firebaseId: String(data.firebaseId ?? ''),
      firstName: String(data.firstName ?? ''),
      lastName: String(data.lastName ?? ''),
      email: String(data.email ?? ''),
      role: role === 'superadmin' ? 'superadmin' : 'admin',
      disabled: Boolean(data.disabled ?? false),
      mustChangePassword: Boolean(data.mustChangePassword ?? false),
      passwordChangedAt: data.passwordChangedAt ? String(data.passwordChangedAt) : null,
      lastSignInAt: data.lastSignInAt ? String(data.lastSignInAt) : null,
      emailVerified: Boolean(data.emailVerified ?? false),
      firebaseCreatedAt: data.firebaseCreatedAt ? String(data.firebaseCreatedAt) : null,
      createdAt: String(data.createdAt ?? new Date().toISOString()),
      updatedAt: String(data.updatedAt ?? new Date().toISOString()),
    };
  }

  async listBackofficeUsers(): Promise<BackofficeUser[]> {
    const response = (await this.client.get('/admin/backoffice-users')) as {
      users?: Record<string, unknown>[];
    };
    const users = Array.isArray(response.users) ? response.users : [];
    return users.map((u) => this.parseBackofficeUser(u));
  }

  async createBackofficeUser(body: CreateBackofficeUserBody): Promise<BackofficeUser> {
    const response = (await this.client.post('/admin/backoffice-users', body as unknown as Record<string, unknown>)) as {
      user?: Record<string, unknown>;
    };
    const user = response.user ?? {};
    return this.parseBackofficeUser(user);
  }

  async updateBackofficeUser(firebaseId: string, body: UpdateBackofficeUserBody): Promise<BackofficeUser> {
    const response = (await this.client.put(`/admin/backoffice-users/${firebaseId}`, body as unknown as Record<string, unknown>)) as {
      user?: Record<string, unknown>;
    };
    const user = response.user ?? {};
    return this.parseBackofficeUser(user);
  }

  async disableBackofficeUser(firebaseId: string): Promise<void> {
    await this.client.post(`/admin/backoffice-users/${firebaseId}/disable`);
  }

  async enableBackofficeUser(firebaseId: string): Promise<void> {
    await this.client.post(`/admin/backoffice-users/${firebaseId}/enable`);
  }

  async deleteBackofficeUser(firebaseId: string): Promise<void> {
    await this.client.delete(`/admin/backoffice-users/${firebaseId}`);
  }

  async resetBackofficeUserPassword(email: string): Promise<{ message: string; link?: string }> {
    const response = (await this.client.post('/admin/backoffice-users/reset-password', { email })) as {
      message?: string;
      link?: string;
    };
    return {
      message: String(response.message ?? 'Password reset email sent'),
      link: response.link ? String(response.link) : undefined,
    };
  }

  // ── Tenant Operators ──

  private parseTenantOperator(data: Record<string, unknown>): TenantOperator {
    const rawRole = String(data.role ?? 'tecnico');
    const role: BackofficeRole =
      rawRole === 'admin' || rawRole === 'business' || rawRole === 'tecnico'
        ? rawRole
        : 'tecnico';

    const disabled = Boolean(data.disabled ?? false);
    const active = data.active !== undefined ? Boolean(data.active) : !disabled;

    return {
      id: data.id ? String(data.id) : undefined,
      tenantId: String(data.tenantId ?? ''),
      tenantName: String(data.tenantName ?? data.tenantId ?? ''),
      username: String(data.username ?? ''),
      displayName: data.displayName ? String(data.displayName) : undefined,
      role,
      hasPassword: Boolean(data.hasPassword ?? false),
      disabled: !active,
      active,
      mustChangePassword: Boolean(data.mustChangePassword ?? false),
      createdAt: data.createdAt ? String(data.createdAt) : undefined,
    };
  }

  async listTenantOperators(): Promise<TenantOperator[]> {
    const response = (await this.client.get('/admin/operators')) as {
      operators?: Record<string, unknown>[];
    };
    const operators = Array.isArray(response.operators) ? response.operators : [];
    return operators.map((o) => this.parseTenantOperator(o));
  }

  async upsertTenantOperator(
    tenantId: string,
    body: UpsertTenantOperatorBody,
  ): Promise<TenantOperator> {
    const response = (await this.client.post(`/admin/operators/${tenantId}`, body as unknown as Record<string, unknown>)) as {
      operator?: Record<string, unknown>;
    };
    return this.parseTenantOperator(response.operator ?? {});
  }

  async updateTenantOperator(
    tenantId: string,
    userId: string,
    body: UpdateTenantOperatorBody,
  ): Promise<TenantOperator> {
    const response = (await this.client.patch(
      `/admin/operators/${tenantId}/${userId}`,
      body as unknown as Record<string, unknown>,
    )) as { operator?: Record<string, unknown> };
    return this.parseTenantOperator(response.operator ?? {});
  }

  async resetTenantOperatorPassword(
    tenantId: string,
    userId: string,
    password: string,
  ): Promise<void> {
    await this.client.post(`/admin/operators/${tenantId}/${userId}/reset-password`, { password });
  }

  async deleteTenantOperator(tenantId: string, userId?: string): Promise<void> {
    const path = userId ? `/admin/operators/${tenantId}/${userId}` : `/admin/operators/${tenantId}`;
    await this.client.delete(path);
  }

  async getTenantStats(tenantId: string): Promise<TenantStats> {
    const response = (await this.client.get(`/admin/tenants/${tenantId}/stats`)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    // indexedProducts/indexCoverage sono i campi nuovi; gli alias *Embedding* restano
    // per compatibilità con un backend non ancora aggiornato.
    return {
      tenantId: String(data.tenantId ?? tenantId),
      totalProducts: Number(data.totalProducts ?? 0),
      indexedProducts: Number(data.indexedProducts ?? data.productsWithEmbeddings ?? 0),
      indexCoverage: Number(data.indexCoverage ?? data.embeddingCoverage ?? 0),
      indexSource: data.indexSource === 'qdrant' ? 'qdrant' : 'mongo',
      indexAvailable: data.indexAvailable !== false,
      documentChunks:
        data.documentChunks === undefined || data.documentChunks === null
          ? null
          : Number(data.documentChunks),
      engineVersion: data.engineVersion === 'v2' ? 'v2' : 'v1',
    };
  }

  /** Id dei prodotti presenti nell'indice attivo del tenant (Mongo v1 / Qdrant v2). */
  async getProductIndexStatus(tenantId: string): Promise<ProductIndexStatus> {
    const response = (await this.client.get(
      `/admin/tenants/${tenantId}/products/index-status`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const ids = Array.isArray(data.indexedIds) ? (data.indexedIds as unknown[]) : [];
    return {
      indexSource: data.indexSource === 'qdrant' ? 'qdrant' : 'mongo',
      indexAvailable: data.indexAvailable !== false,
      engineVersion: data.engineVersion === 'v2' ? 'v2' : 'v1',
      indexedIds: new Set(ids.map((id) => String(id))),
    };
  }

  // ── Products ──

  async listProducts(tenantId: string, search = '', enabled?: boolean): Promise<Product[]> {
    const query = buildQuery({
      includeEmbedding: 'false',
      search,
      enabled: enabled === undefined ? undefined : String(enabled),
    });
    const response = (await this.client.get(`/products${query}`, {
      'x-tenant-id': tenantId,
    })) as { data: unknown };

    let data: Record<string, unknown>[] = [];
    const rawData = response.data;
    if (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
      const products = (rawData as Record<string, unknown>).products;
      data = Array.isArray(products) ? (products as Record<string, unknown>[]) : [];
    } else if (Array.isArray(rawData)) {
      data = rawData as Record<string, unknown>[];
    } else if (Array.isArray(response)) {
      data = response as Record<string, unknown>[];
    }

    const parseStringList = (value: unknown): string[] => {
      if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
      return [];
    };

    return data.map((e) => {
      return {
        id: String(e.id ?? e._id ?? ''),
        name: String(e.name ?? ''),
        category: String(e.category ?? ''),
        subcategory: e.subcategory ? String(e.subcategory) : undefined,
        description: e.description ? String(e.description) : undefined,
        surfaces: parseStringList(e.surfaces),
        formati: parseStringList(e.formati),
        target: parseStringList(e.target),
        technicalNote: e.technicalNote ? String(e.technicalNote) : undefined,
        url: e.url ? String(e.url) : undefined,
        imageUrl: e.imageUrl ? String(e.imageUrl) : undefined,
        isEnabled: Boolean(e.isEnabled ?? true),
      } as Product;
    });
  }

  async updateProduct(
    tenantId: string,
    productId: string,
    body: { isEnabled?: boolean },
  ): Promise<void> {
    await this.client.patch(`/products/${productId}`, body, {
      'x-tenant-id': tenantId,
    });
  }

  // ── Chat ──

  async sendChatMessage(
    tenantId: string,
    message: string,
    options: { history?: { role: string; content: string }[]; conversationId?: string } = {},
  ): Promise<Record<string, unknown>> {
    const body: Record<string, unknown> = { message, history: options.history ?? [] };
    if (options.conversationId) {
      body.conversationId = options.conversationId;
    }
    return (await this.client.post('/chat', body, { 'x-tenant-id': tenantId })) as Record<
      string,
      unknown
    >;
  }

  // ── Sector Config ──

  async getSectorConfig(tenantId: string): Promise<SectorConfig | null> {
    try {
      const response = (await this.client.get(`/admin/sector-config/${tenantId}`)) as {
        data: Record<string, unknown> | null;
      };
      const data = response.data;
      if (!data) return null;
      return {
        tenantId: String(data.tenantId ?? tenantId),
        sector: String(data.sector ?? ''),
        evaluationCriteria: Array.isArray(data.evaluationCriteria)
          ? data.evaluationCriteria.map(String)
          : [],
        customPrompt: data.customPrompt ? String(data.customPrompt) : undefined,
        feedbackAlertEmail: Array.isArray(data.feedbackAlertEmail)
          ? data.feedbackAlertEmail.filter((v): v is string => typeof v === 'string')
          : typeof data.feedbackAlertEmail === 'string' && data.feedbackAlertEmail.trim()
            ? [String(data.feedbackAlertEmail).trim()]
            : undefined,
        feedbackAlertEnabled: Boolean(data.feedbackAlertEnabled ?? false),
        feedbackAlertSubjectTemplate: data.feedbackAlertSubjectTemplate
          ? String(data.feedbackAlertSubjectTemplate)
          : undefined,
        feedbackAlertBodyTemplate: data.feedbackAlertBodyTemplate
          ? String(data.feedbackAlertBodyTemplate)
          : undefined,
        feedbackAlertDefaultSubjectTemplate: data.feedbackAlertDefaultSubjectTemplate
          ? String(data.feedbackAlertDefaultSubjectTemplate)
          : undefined,
        feedbackAlertDefaultBodyTemplate: data.feedbackAlertDefaultBodyTemplate
          ? String(data.feedbackAlertDefaultBodyTemplate)
          : undefined,
        weeklyReport: toWeeklyReportConfig(data.weeklyReport),
        userReportForwardEmails: Array.isArray(data.userReportForwardEmails)
          ? data.userReportForwardEmails.map(String)
          : [],
        features: {
          flaggedAnswersEnabled: Boolean(data.flaggedAnswersEnabled ?? false),
        },
      };
    } catch (e) {
      if ((e as Error)?.message?.includes('404')) return null;
      throw e;
    }
  }

  async updateSectorConfig(
    tenantId: string,
    body: {
      sector: string;
      evaluationCriteria: string[];
      customPrompt?: string;
      feedbackAlertEmail?: string[];
      feedbackAlertEnabled?: boolean;
      feedbackAlertSubjectTemplate?: string;
      feedbackAlertBodyTemplate?: string;
    },
  ): Promise<SectorConfig> {
    const response = (await this.client.put(`/admin/sector-config/${tenantId}`, body)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    return {
      tenantId: String(data.tenantId ?? tenantId),
      sector: String(data.sector ?? ''),
      evaluationCriteria: Array.isArray(data.evaluationCriteria)
        ? data.evaluationCriteria.map(String)
        : [],
      customPrompt: data.customPrompt ? String(data.customPrompt) : undefined,
      feedbackAlertEmail: Array.isArray(data.feedbackAlertEmail)
        ? data.feedbackAlertEmail.filter((v): v is string => typeof v === 'string')
        : typeof data.feedbackAlertEmail === 'string' && data.feedbackAlertEmail.trim()
          ? [String(data.feedbackAlertEmail).trim()]
          : undefined,
      feedbackAlertEnabled: Boolean(data.feedbackAlertEnabled ?? false),
      feedbackAlertSubjectTemplate: data.feedbackAlertSubjectTemplate
        ? String(data.feedbackAlertSubjectTemplate)
        : undefined,
      feedbackAlertBodyTemplate: data.feedbackAlertBodyTemplate
        ? String(data.feedbackAlertBodyTemplate)
        : undefined,
      features: {
        flaggedAnswersEnabled: Boolean(data.flaggedAnswersEnabled ?? false),
      },
    };
  }

  /** Salva solo gli interruttori dati: il resto della config resta com'è. */
  async updateAppFeatures(tenantId: string, patch: Partial<AppFeatures>): Promise<void> {
    await this.client.put(`/admin/sector-config/${tenantId}`, patch);
  }

  /**
   * Anteprima dell'email di alert feedback negativo con dati di esempio.
   * `subjectTemplate`/`bodyTemplate` sono opzionali: se passati (anche stringa
   * vuota) testano la bozza corrente senza salvarla; se `undefined` il backend
   * usa i template salvati sul tenant.
   */
  async previewFeedbackAlert(
    tenantId: string,
    body: { subjectTemplate?: string; bodyTemplate?: string } = {},
  ): Promise<FeedbackAlertPreview> {
    const response = (await this.client.post(
      `/admin/sector-config/${tenantId}/feedback-alert/preview`,
      body,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    return {
      subject: String(data.subject ?? ''),
      text: String(data.text ?? ''),
      html: String(data.html ?? ''),
      placeholders: Array.isArray(data.placeholders)
        ? data.placeholders
            .filter((p): p is { token: string; description: string } =>
              !!p && typeof (p as { token?: unknown }).token === 'string',
            )
            .map((p) => ({ token: String(p.token), description: String(p.description ?? '') }))
        : [],
    };
  }

  /** Salva la config del riepilogo settimanale; il backend riprogramma subito il job. */
  async updateWeeklyReport(tenantId: string, body: WeeklyReportConfig): Promise<WeeklyReportConfig> {
    const response = (await this.client.put(`/admin/tenants/${tenantId}/weekly-report`, { ...body })) as {
      data: unknown;
    };
    return toWeeklyReportConfig(response.data);
  }

  /** dryRun: calcola senza inviare (con l'HTML). `to`: invio di prova a questi indirizzi. */
  async runWeeklyReport(
    tenantId: string,
    body: { dryRun?: boolean; to?: string[] },
  ): Promise<WeeklyReportResult> {
    const response = (await this.client.post(`/admin/tenants/${tenantId}/weekly-report`, body)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    return {
      to: Array.isArray(data.to) ? data.to.map(String) : [],
      subject: String(data.subject ?? ''),
      conversations: Number(data.conversations ?? 0),
      sent: data.sent === true,
      skipped: data.skipped ? String(data.skipped) : undefined,
      html: data.html ? String(data.html) : undefined,
    };
  }

  /**
   * Invia un'email di alert di TEST (dati di esempio, oggetto con prefisso
   * "[TEST] "). `to` opzionale: stringa (anche con virgole) o array; se assente
   * usa i destinatari configurati sul tenant.
   */
  async testFeedbackAlert(
    tenantId: string,
    body: { to?: string; subjectTemplate?: string; bodyTemplate?: string } = {},
  ): Promise<{ sent: boolean; to?: string[]; messageId?: string; reason?: string }> {
    const response = (await this.client.post(
      `/admin/sector-config/${tenantId}/feedback-alert/test`,
      body,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const to = Array.isArray(data.to)
      ? data.to.filter((v): v is string => typeof v === 'string')
      : typeof data.to === 'string' && data.to.trim()
        ? [String(data.to).trim()]
        : undefined;
    return {
      sent: Boolean(data.sent ?? false),
      to,
      messageId: data.messageId ? String(data.messageId) : undefined,
      reason: data.reason ? String(data.reason) : undefined,
    };
  }

  // ── Battery Test ──

  async runBatteryTest(
    tenantId: string,
    questions: string[],
    options: { onProgress?: (result: ChatTestResult, index: number) => void } = {},
  ): Promise<ChatTestBatch> {
    const results: ChatTestBatch['results'] = [];
    let successful = 0;
    let failed = 0;
    let totalResponseTime = 0;

    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const start = performance.now();
      try {
        const response = await this.sendChatMessage(tenantId, question);
        const responseTime = Math.round(performance.now() - start);
        totalResponseTime += responseTime;

        const products = ((response.products as Record<string, unknown>[]) ?? []).map((p) => ({
          id: String(p.id ?? ''),
          name: String(p.name ?? ''),
        }));

        const result: ChatTestResult = {
          question,
          success: true,
          reply: response.reply ? String(response.reply) : undefined,
          suggestedReplies: Array.isArray(response.suggestedReplies)
            ? response.suggestedReplies.map(String)
            : [],
          products,
          responseTime,
        };
        results.push(result);
        successful++;
        options.onProgress?.(result, i);
      } catch (error) {
        const responseTime = Math.round(performance.now() - start);
        totalResponseTime += responseTime;
        const result: ChatTestResult = {
          question,
          success: false,
          error: (error as Error).message,
          suggestedReplies: [],
          products: [],
          responseTime,
        };
        results.push(result);
        failed++;
        options.onProgress?.(result, i);
      }
    }

    return {
      results,
      successful,
      failed,
      averageResponseTime: results.length ? Math.round(totalResponseTime / results.length) : 0,
    };
  }

  async runEvaluatedBatteryTest(
    tenantId: string,
    questions: string[],
    evaluateWithAI = true,
  ): Promise<EvaluatedChatTestBatch> {
    return (await this.client.post(
      '/admin/battery-test-evaluated',
      { tenantId, questions, evaluateWithAI },
    )) as EvaluatedChatTestBatch;
  }

  // ── Logs / Analytics ──

  async getRequestLogs(
    tenantId: string,
    { limit = 100, offset = 0, startDate, endDate, path, statusCode }: {
      limit?: number;
      offset?: number;
      startDate?: string;
      endDate?: string;
      path?: string;
      statusCode?: number;
    } = {},
  ): Promise<PaginatedResponse<RequestLog>> {
    const query = buildQuery({
      limit,
      offset,
      startDate,
      endDate,
      path,
      statusCode,
    });
    const response = (await this.client.get(`/admin/tenants/${tenantId}/logs/requests${query}`)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    const logs = (data.logs as Record<string, unknown>[]) ?? [];
    return {
      items: logs.map((e) => ({
        // I documenti Mongo espongono `_id` e `timestamp`: `id`/`createdAt`
        // esistono solo su client che li normalizzano già.
        id: String(e.id ?? e._id ?? ''),
        path: String(e.path ?? ''),
        method: String(e.method ?? ''),
        statusCode: Number(e.statusCode ?? 0),
        createdAt: String(e.createdAt ?? e.timestamp ?? ''),
      })),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
    };
  }

  async getErrorLogs(
    tenantId: string,
    { limit = 100, offset = 0, startDate, endDate, path, source, statusCode }: {
      limit?: number;
      offset?: number;
      startDate?: string;
      endDate?: string;
      /** Ricerca parziale sul path della richiesta che ha generato l'errore. */
      path?: string;
      /** Match esatto sul prefisso `[Source]` del messaggio. */
      source?: string;
      statusCode?: number;
    } = {},
  ): Promise<PaginatedResponse<ErrorLog>> {
    const query = buildQuery({ limit, offset, startDate, endDate, path, source, statusCode });
    const response = (await this.client.get(`/admin/tenants/${tenantId}/logs/errors${query}`)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    const logs = (data.logs as Record<string, unknown>[]) ?? [];
    return {
      items: logs.map((e) => ({
        id: String(e.id ?? e._id ?? ''),
        path: String(e.path ?? ''),
        message: String(e.message ?? ''),
        statusCode: e.statusCode ? Number(e.statusCode) : undefined,
        createdAt: String(e.createdAt ?? e.timestamp ?? ''),
        method: e.method ? String(e.method) : undefined,
        source: e.source ? String(e.source) : undefined,
        stack: e.stack ? String(e.stack) : undefined,
        requestId: e.requestId ? String(e.requestId) : undefined,
      })),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
    };
  }

  async getConversationLogs(
    tenantId: string,
    { limit = 100, offset = 0, startDate, endDate, firebaseId }: {
      limit?: number;
      offset?: number;
      startDate?: string;
      endDate?: string;
      firebaseId?: string;
    } = {},
  ): Promise<PaginatedResponse<ConversationLog>> {
    const query = buildQuery({ limit, offset, startDate, endDate, firebaseId });
    const response = (await this.client.get(
      `/admin/tenants/${tenantId}/logs/conversations${query}`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const logs = (data.logs as Record<string, unknown>[]) ?? [];
    return {
      items: logs.map((e) => ({
        id: String(e.id ?? ''),
        conversationId: String(e.conversationId ?? ''),
        summary: e.summary ? String(e.summary) : undefined,
        firebaseId: e.firebaseId ? String(e.firebaseId) : undefined,
        userEmail: e.userEmail ? String(e.userEmail) : undefined,
        userDisplayName: e.userDisplayName ? String(e.userDisplayName) : undefined,
        messageCount: Number(e.messageCount ?? 0),
        firstMessageAt: e.firstMessageAt ? String(e.firstMessageAt) : undefined,
        lastMessageAt: e.lastMessageAt ? String(e.lastMessageAt) : undefined,
        createdAt: e.createdAt ? String(e.createdAt) : undefined,
      })),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
    };
  }

  async getConversationDetail(tenantId: string, conversationId: string): Promise<Record<string, unknown>> {
    const response = (await this.client.get(
      `/admin/tenants/${tenantId}/logs/conversations/${conversationId}`,
    )) as { data: Record<string, unknown> };
    return response.data ?? {};
  }

  async getChatMessageLogs(
    tenantId: string,
    { limit = 100, offset = 0, conversationId, startDate, endDate }: {
      limit?: number;
      offset?: number;
      conversationId?: string;
      startDate?: string;
      endDate?: string;
    } = {},
  ): Promise<PaginatedResponse<ChatMessageLog>> {
    const query = buildQuery({ limit, offset, conversationId, startDate, endDate });
    const response = (await this.client.get(
      `/admin/tenants/${tenantId}/logs/chat-messages${query}`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const logs = (data.logs as Record<string, unknown>[]) ?? [];
    return {
      items: logs.map((e) => ({
        id: String(e.id ?? e._id ?? ''),
        conversationId: String(e.conversationId ?? ''),
        role: String(e.role ?? ''),
        content: String(e.content ?? e.message ?? ''),
        createdAt: e.timestamp ? String(e.timestamp) : String(e.createdAt ?? ''),
      })),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
    };
  }

  async getFeedbackLogs(
    tenantId: string,
    { limit = 100, offset = 0, conversationId, startDate, endDate, rating }: {
      limit?: number;
      offset?: number;
      conversationId?: string;
      startDate?: string;
      endDate?: string;
      rating?: 'positive' | 'negative';
    } = {},
  ): Promise<PaginatedResponse<FeedbackLog>> {
    const query = buildQuery({ limit, offset, conversationId, startDate, endDate, rating });
    const response = (await this.client.get(
      `/admin/tenants/${tenantId}/logs/feedback${query}`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const logs = (data.logs as Record<string, unknown>[]) ?? [];
    return {
      items: logs.map((e) => ({
        id: String(e.id ?? e._id ?? ''),
        conversationId: String(e.conversationId ?? ''),
        messageId: String(e.messageId ?? ''),
        rating: (e.rating === 'negative' ? 'negative' : 'positive') as FeedbackLog['rating'],
        comment: e.comment ? String(e.comment) : undefined,
        messageText: e.messageText ? String(e.messageText) : undefined,
        firebaseId: e.firebaseId ? String(e.firebaseId) : undefined,
        userEmail: e.userEmail ? String(e.userEmail) : undefined,
        ip: e.ip ? String(e.ip) : undefined,
        userAgent: e.userAgent ? String(e.userAgent) : undefined,
        createdAt: e.timestamp ? String(e.timestamp) : String(e.createdAt ?? ''),
        reviewStatus: (['pending', 'confirmed_negative', 'not_negative', 'resolved'].includes(
          String(e.reviewStatus),
        )
          ? e.reviewStatus
          : 'pending') as FeedbackLog['reviewStatus'],
        reviewNote: e.reviewNote ? String(e.reviewNote) : undefined,
        reviewedBy: e.reviewedBy ? String(e.reviewedBy) : null,
        reviewedAt: e.reviewedAt ? String(e.reviewedAt) : null,
        correctionId: e.correctionId ? String(e.correctionId) : null,
      })),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
    };
  }

  async sendFeedbackAlert(tenantId: string, messageId: string): Promise<{ sent: boolean; to?: string[]; messageId?: string; reason?: string }> {
    const response = (await this.client.post(
      `/admin/tenants/${tenantId}/logs/feedback/${encodeURIComponent(messageId)}/send-alert`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const to = Array.isArray(data.to)
      ? data.to.filter((v): v is string => typeof v === 'string')
      : typeof data.to === 'string' && data.to.trim()
        ? [String(data.to).trim()]
        : undefined;
    return {
      sent: Boolean(data.sent ?? false),
      to,
      messageId: data.messageId ? String(data.messageId) : undefined,
      reason: data.reason ? String(data.reason) : undefined,
    };
  }

  // ── Segnalazioni utenti (idee e bug dall'app tenant) ──

  /** Lista cross-tenant: senza tenantId il backend aggrega tutti i tenant. */
  async listUserReports({
    tenantId,
    status,
    type,
    search,
    startDate,
    endDate,
    limit = 25,
    offset = 0,
  }: {
    tenantId?: string;
    status?: UserReportStatus | 'all';
    type?: UserReportType | 'all';
    search?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<PaginatedResponse<UserReport> & { truncated: boolean }> {
    const query = buildQuery({
      tenantId,
      status: status === 'all' ? undefined : status,
      type: type === 'all' ? undefined : type,
      search,
      startDate,
      endDate,
      limit,
      offset,
    });
    const response = (await this.client.get(`/admin/user-reports${query}`)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    const rows = (data.reports as Record<string, unknown>[]) ?? [];
    return {
      items: rows.map((r) => this.parseUserReport(r)),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
      truncated: Boolean(data.truncated ?? false),
    };
  }

  async getUserReportStats(tenantId?: string): Promise<UserReportStats> {
    const response = (await this.client.get(
      `/admin/user-reports/stats${buildQuery({ tenantId })}`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const byStatus = (data.byStatus ?? {}) as Record<string, number>;
    const statuses: UserReportStatus[] = ['new', 'triaged', 'planned', 'done', 'declined'];
    return {
      byStatus: Object.fromEntries(
        statuses.map((s) => [s, Number(byStatus[s] ?? 0)]),
      ) as Record<UserReportStatus, number>,
      total: Number(data.total ?? 0),
    };
  }

  async updateUserReport(
    tenantId: string,
    id: string,
    patch: { status?: UserReportStatus; adminNote?: string; reviewedBy?: string | null },
  ): Promise<UserReport> {
    const response = (await this.client.patch(
      `/admin/tenants/${encodeURIComponent(tenantId)}/user-reports/${encodeURIComponent(id)}`,
      patch as Record<string, unknown>,
    )) as { data: Record<string, unknown> };
    return this.parseUserReport(response.data ?? {});
  }

  /** Inoltra la segnalazione ai referenti impostati per il tenant in Sector Config. */
  async forwardUserReport(tenantId: string, id: string): Promise<{ to: string[]; forwardedAt: string }> {
    const response = (await this.client.post(
      `/admin/tenants/${encodeURIComponent(tenantId)}/user-reports/${encodeURIComponent(id)}/forward`,
      {},
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    return {
      to: Array.isArray(data.to) ? data.to.map(String) : [],
      forwardedAt: String(data.forwardedAt ?? ''),
    };
  }

  /** Salva solo i referenti per le segnalazioni, senza toccare il resto della config. */
  async updateUserReportForwardEmails(tenantId: string, emails: string[]): Promise<void> {
    await this.client.put(`/admin/sector-config/${tenantId}`, { userReportForwardEmails: emails });
  }

  private parseUserReport(r: Record<string, unknown>): UserReport {
    const statuses: UserReportStatus[] = ['new', 'triaged', 'planned', 'done', 'declined'];
    const status = statuses.includes(String(r.status) as UserReportStatus)
      ? (r.status as UserReportStatus)
      : 'new';
    return {
      id: String(r.id ?? r._id ?? ''),
      tenantId: String(r.tenantId ?? ''),
      type: r.type === 'bug' ? 'bug' : 'feature',
      message: String(r.message ?? ''),
      anonymous: Boolean(r.anonymous ?? false),
      firebaseId: r.firebaseId ? String(r.firebaseId) : null,
      userEmail: r.userEmail ? String(r.userEmail) : undefined,
      contactEmail: r.contactEmail ? String(r.contactEmail) : undefined,
      appVersion: r.appVersion ? String(r.appVersion) : undefined,
      path: r.path ? String(r.path) : undefined,
      locale: r.locale ? String(r.locale) : undefined,
      userAgent: r.userAgent ? String(r.userAgent) : undefined,
      status,
      adminNote: r.adminNote ? String(r.adminNote) : undefined,
      reviewedBy: r.reviewedBy ? String(r.reviewedBy) : null,
      reviewedAt: r.reviewedAt ? String(r.reviewedAt) : null,
      forwardedAt: r.forwardedAt ? String(r.forwardedAt) : null,
      forwardedTo: Array.isArray(r.forwardedTo) ? r.forwardedTo.map(String) : undefined,
      createdAt: String(r.createdAt ?? ''),
    };
  }

  async getDailyStats(
    tenantId: string,
    { limit = 30, offset = 0, startDate, endDate }: {
      limit?: number;
      offset?: number;
      startDate?: string;
      endDate?: string;
    } = {},
  ): Promise<PaginatedResponse<DailyStats>> {
    const query = buildQuery({ limit, offset, startDate, endDate });
    const response = (await this.client.get(
      `/admin/tenants/${tenantId}/logs/daily-stats${query}`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const logs = (data.logs as Record<string, unknown>[]) ?? [];
    return {
      items: logs.map((e) => ({
        date: String(e.date ?? ''),
        requests: Number(e.requests ?? 0),
        errors: Number(e.errors ?? 0),
        conversations: Number(e.conversations ?? 0),
      })),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
    };
  }

  async getStatsSummary(tenantId: string, days = 7): Promise<StatsSummary> {
    try {
      const response = (await this.client.get(`/admin/tenants/${tenantId}/stats/summary?days=${days}`)) as {
        data: Record<string, unknown>;
      };
      const data = response.data ?? {};
      return {
        totalRequests: Number(data.totalRequests ?? 0),
        totalErrors: Number(data.totalErrors ?? 0),
        totalConversations: Number(data.totalConversations ?? 0),
        averageResponseTime: Number(data.averageResponseTime ?? 0),
        averageChatbotResponseTime: Number(data.averageChatbotResponseTime ?? 0),
      };
    } catch (e) {
      // Fallback: se il backend non espone /stats/summary, aggrega dai daily stats.
      if ((e as Error)?.message?.includes('404')) {
        const daily = await this.getDailyStats(tenantId, { limit: days });
        const summary = daily.items.reduce<StatsSummary>(
          (acc, d) => ({
            totalRequests: acc.totalRequests + (d.requests ?? 0),
            totalErrors: acc.totalErrors + (d.errors ?? 0),
            totalConversations: acc.totalConversations + (d.conversations ?? 0),
            averageResponseTime: 0,
            averageChatbotResponseTime: 0,
          }),
          {
            totalRequests: 0,
            totalErrors: 0,
            totalConversations: 0,
            averageResponseTime: 0,
            averageChatbotResponseTime: 0,
          },
        );
        return summary;
      }
      throw e;
    }
  }

  // ── System Monitoring ──

  async getSystemHealth(): Promise<SystemHealth> {
    const response = (await this.client.get('/admin/system/health')) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    return {
      status: String(data.status ?? 'degraded') as SystemHealth['status'],
      uptime: Number(data.uptime ?? 0),
      db: String(data.db ?? 'unknown'),
      redis: String(data.redis ?? 'unknown'),
      timestamp: String(data.timestamp ?? new Date().toISOString()),
    };
  }

  async getSystemMetrics(): Promise<SystemMetrics> {
    const response = (await this.client.get('/admin/system/metrics')) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    return {
      cpu: data.cpu ? (data.cpu as SystemMetrics['cpu']) : null,
      memory: data.memory ? (data.memory as SystemMetrics['memory']) : null,
      disk: data.disk ? (data.disk as SystemMetrics['disk']) : null,
      loadAvg: data.loadAvg ? (data.loadAvg as SystemMetrics['loadAvg']) : null,
      timestamp: String(data.timestamp ?? new Date().toISOString()),
    };
  }

  async getUnusedResources(days = 30): Promise<UnusedResources> {
    const response = (await this.client.get(`/admin/system/unused-resources?days=${days}`)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    return {
      inactiveTenants: Array.isArray(data.inactiveTenants) ? (data.inactiveTenants as UnusedResources['inactiveTenants']) : [],
      productsNotIndexed: Array.isArray(data.productsNotIndexed ?? data.productsWithoutEmbedding)
        ? ((data.productsNotIndexed ?? data.productsWithoutEmbedding) as UnusedResources['productsNotIndexed'])
        : [],
      disabledUsers: Array.isArray(data.disabledUsers) ? (data.disabledUsers as UnusedResources['disabledUsers']) : [],
      staleConversations: Number(data.staleConversations ?? 0),
      days: Number(data.days ?? days),
      checkedAt: String(data.checkedAt ?? new Date().toISOString()),
    };
  }

  async getAlertSettings(): Promise<AlertSettings> {
    const response = (await this.client.get('/admin/system/alert-settings')) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    return this.parseAlertSettings(data);
  }

  async updateAlertSettings(body: Partial<AlertSettings>): Promise<AlertSettings> {
    const response = (await this.client.put('/admin/system/alert-settings', body as Record<string, unknown>)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    return this.parseAlertSettings(data);
  }

  async checkAlerts(): Promise<AlertCheckResult> {
    const response = (await this.client.post('/admin/system/check-alerts')) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    return {
      checked: Boolean(data.checked ?? false),
      reason: data.reason ? String(data.reason) : undefined,
      status: data.status as AlertCheckResult['status'],
      metrics: data.metrics as AlertCheckResult['metrics'],
      health: data.health as AlertCheckResult['health'],
      newlyCritical: Array.isArray(data.newlyCritical) ? data.newlyCritical.map(String) : undefined,
      recovered: Array.isArray(data.recovered) ? data.recovered.map(String) : undefined,
      emailSent: data.emailSent !== undefined ? Boolean(data.emailSent) : undefined,
    };
  }

  async getSystemDomains(): Promise<DomainCheckResult[]> {
    const response = (await this.client.get('/admin/system/domains')) as { data: unknown[] };
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map((d) => this.parseDomainCheckResult(d as Record<string, unknown>));
  }

  async updateSystemDomains(domains: MonitoredDomain[]): Promise<MonitoredDomain[]> {
    const response = (await this.client.put('/admin/system/domains', { monitoredDomains: domains })) as {
      data: unknown[];
    };
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map((d) => this.parseMonitoredDomain(d as Record<string, unknown>));
  }

  private parseDomainCheckResult(data: Record<string, unknown>): DomainCheckResult {
    return {
      domain: String(data.domain ?? ''),
      label: String(data.label ?? ''),
      ok: Boolean(data.ok ?? false),
      dnsResolved: Boolean(data.dnsResolved ?? false),
      statusCode: data.statusCode ? Number(data.statusCode) : undefined,
      certExpiryDate: data.certExpiryDate ? String(data.certExpiryDate) : undefined,
      certDaysRemaining: data.certDaysRemaining !== undefined ? Number(data.certDaysRemaining) : undefined,
      error: data.error ? String(data.error) : undefined,
      responseTimeMs: data.responseTimeMs !== undefined ? Number(data.responseTimeMs) : undefined,
      checkedAt: String(data.checkedAt ?? new Date().toISOString()),
    };
  }

  private parseMonitoredDomain(data: Record<string, unknown>): MonitoredDomain {
    return {
      _id: data._id ? String(data._id) : undefined,
      domain: String(data.domain ?? ''),
      label: String(data.label ?? ''),
    };
  }

  // ── Backups ──

  async getBackupsOverview(): Promise<BackupOverviewItem[]> {
    const response = (await this.client.get('/admin/backups/overview')) as { data: Record<string, unknown>[] };
    const data = response.data ?? [];
    return data.map((e) => this.parseBackupOverviewItem(e));
  }

  async updateBackupConfig(
    tenantId: string,
    body: Partial<Pick<BackupConfig, 'enabled' | 'cronExpression' | 'retentionDays'>>,
  ): Promise<BackupConfig> {
    const response = (await this.client.put(`/admin/backups/${tenantId}/config`, body as Record<string, unknown>)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    return {
      tenantId: String(data.tenantId ?? tenantId),
      enabled: Boolean(data.enabled ?? false),
      cronExpression: String(data.cronExpression ?? '0 3 * * *'),
      retentionDays: Number(data.retentionDays ?? 30),
    };
  }

  async triggerBackup(tenantId: string): Promise<BackupRun> {
    const response = (await this.client.post(`/admin/backups/${tenantId}/run`)) as { data: Record<string, unknown> };
    return this.parseBackupRun(response.data ?? {});
  }

  async getBackupRuns(
    tenantId: string,
    { limit = 20, offset = 0 }: { limit?: number; offset?: number } = {},
  ): Promise<PaginatedResponse<BackupRun>> {
    const query = buildQuery({ limit, offset });
    const response = (await this.client.get(`/admin/backups/${tenantId}/runs${query}`)) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    const runs = (data.runs as Record<string, unknown>[]) ?? [];
    return {
      items: runs.map((r) => this.parseBackupRun(r)),
      total: Number(data.total ?? 0),
      limit: Number(data.limit ?? limit),
      offset: Number(data.offset ?? offset),
    };
  }

  async getBackupFiles(tenantId: string): Promise<BackupFile[]> {
    const response = (await this.client.get(`/admin/backups/${tenantId}/files`)) as { data: Record<string, unknown>[] };
    const data = response.data ?? [];
    return data.map((f) => ({
      key: String(f.key ?? ''),
      filename: String(f.filename ?? ''),
      size: Number(f.size ?? 0),
      lastModified: String(f.lastModified ?? ''),
    }));
  }

  async getBackupDownloadUrl(tenantId: string, filename: string): Promise<string> {
    const response = (await this.client.get(
      `/admin/backups/${tenantId}/files/${encodeURIComponent(filename)}/download`,
    )) as { data: { url: string } };
    return response.data.url;
  }

  async deleteBackupFile(tenantId: string, filename: string): Promise<void> {
    await this.client.delete(`/admin/backups/${tenantId}/files/${encodeURIComponent(filename)}`);
  }

  private parseBackupOverviewItem(e: Record<string, unknown>): BackupOverviewItem {
    const lastRunRaw = e.lastRun as Record<string, unknown> | null;
    return {
      tenantId: String(e.tenantId ?? ''),
      name: String(e.name ?? e.tenantId ?? ''),
      enabled: Boolean(e.enabled ?? false),
      cronExpression: String(e.cronExpression ?? '0 3 * * *'),
      retentionDays: Number(e.retentionDays ?? 30),
      lastRun: lastRunRaw
        ? {
            status: (lastRunRaw.status as BackupLastRun['status']) ?? 'error',
            triggeredBy: (lastRunRaw.triggeredBy as BackupLastRun['triggeredBy']) ?? 'manual',
            startedAt: String(lastRunRaw.startedAt ?? ''),
            finishedAt: lastRunRaw.finishedAt ? String(lastRunRaw.finishedAt) : undefined,
            sizeBytes:
              lastRunRaw.sizeBytes !== undefined && lastRunRaw.sizeBytes !== null
                ? Number(lastRunRaw.sizeBytes)
                : undefined,
            error: lastRunRaw.error ? String(lastRunRaw.error) : undefined,
          }
        : null,
    };
  }

  private parseBackupRun(e: Record<string, unknown>): BackupRun {
    return {
      id: String(e.id ?? e._id ?? ''),
      tenantId: String(e.tenantId ?? ''),
      status: (e.status as BackupRun['status']) ?? 'error',
      triggeredBy: (e.triggeredBy as BackupRun['triggeredBy']) ?? 'manual',
      startedAt: String(e.startedAt ?? ''),
      finishedAt: e.finishedAt ? String(e.finishedAt) : undefined,
      durationMs: e.durationMs !== undefined && e.durationMs !== null ? Number(e.durationMs) : undefined,
      sizeBytes: e.sizeBytes !== undefined && e.sizeBytes !== null ? Number(e.sizeBytes) : undefined,
      collectionsCount:
        e.collectionsCount !== undefined && e.collectionsCount !== null ? Number(e.collectionsCount) : undefined,
      documentsCount:
        e.documentsCount !== undefined && e.documentsCount !== null ? Number(e.documentsCount) : undefined,
      deletedByRetention: e.deletedByRetention !== undefined ? Number(e.deletedByRetention) : undefined,
      error: e.error ? String(e.error) : undefined,
    };
  }

  private parseAlertSettings(data: Record<string, unknown>): AlertSettings {
    const thresholds = (data.thresholds ?? {}) as Record<string, unknown>;
    const rawUrls = Array.isArray(data.monitoredUrls) ? data.monitoredUrls : [];
    const rawDomains = Array.isArray(data.monitoredDomains) ? data.monitoredDomains : [];
    return {
      _id: data._id ? String(data._id) : undefined,
      enabled: Boolean(data.enabled ?? true),
      emailEnabled: Boolean(data.emailEnabled ?? true),
      emailRecipients: Array.isArray(data.emailRecipients)
        ? (data.emailRecipients as unknown[]).filter((v): v is string => typeof v === 'string')
        : [],
      monitoredUrls: rawUrls.map((u) => {
        const item = u as Record<string, unknown>;
        return {
          _id: item._id ? String(item._id) : undefined,
          url: String(item.url ?? ''),
          label: String(item.label ?? ''),
          method: (item.method === 'GET' ? 'GET' : 'HEAD'),
          expectedStatus: Number(item.expectedStatus ?? 200),
          timeoutMs: Number(item.timeoutMs ?? 10000),
        };
      }),
      monitoredDomains: rawDomains.map((d) => this.parseMonitoredDomain(d as Record<string, unknown>)),
      thresholds: {
        cpu: Number(thresholds.cpu ?? 85),
        memory: Number(thresholds.memory ?? 85),
        disk: Number(thresholds.disk ?? 90),
        loadAvg: Number(thresholds.loadAvg ?? 0),
        errorRate: Number(thresholds.errorRate ?? 10),
      },
      checkIntervalMs: Number(data.checkIntervalMs ?? 60000),
      lastAlertSentAt: data.lastAlertSentAt ? String(data.lastAlertSentAt) : undefined,
      lastAlertStatus: data.lastAlertStatus as AlertSettings['lastAlertStatus'],
      lastCheckAt: data.lastCheckAt ? String(data.lastCheckAt) : undefined,
    };
  }
  // ── Staging ──

  async getStagingOverview(): Promise<StagingOverview> {
    const response = (await this.stagingClient.get('/admin/staging/overview')) as {
      data: Record<string, unknown>;
    };
    const data = response.data ?? {};
    const mainDb = (data.mainDb ?? {}) as Record<string, unknown>;
    const parseDbStats = (
      raw: unknown,
    ): { collections: number; docs: number } | null => {
      if (!raw || typeof raw !== 'object') return null;
      const stats = raw as Record<string, unknown>;
      return {
        collections: Number(stats.collections ?? 0),
        docs: Number(stats.docs ?? 0),
      };
    };
    return {
      nodeEnv: String(data.nodeEnv ?? ''),
      tenantDbPrefix: String(data.tenantDbPrefix ?? ''),
      prodTenantDbPrefix: String(data.prodTenantDbPrefix ?? ''),
      syncEnabled: Boolean(data.syncEnabled ?? false),
      mainDb: {
        prod: String(mainDb.prod ?? ''),
        staging: String(mainDb.staging ?? ''),
        prodStats: parseDbStats(mainDb.prodStats),
        stagingStats: parseDbStats(mainDb.stagingStats) ?? { collections: 0, docs: 0 },
      },
      tenants: (Array.isArray(data.tenants) ? data.tenants : []).map((t) => {
        const tenant = t as Record<string, unknown>;
        return {
          tenantId: String(tenant.tenantId ?? ''),
          prodDocs: Number(tenant.prodDocs ?? 0),
          stagingDocs: Number(tenant.stagingDocs ?? 0),
          collections: Number(tenant.collections ?? 0),
          lastSyncAt: tenant.lastSyncAt ? String(tenant.lastSyncAt) : null,
        };
      }),
    };
  }

  async syncStaging(req: StagingSyncRequest): Promise<StagingSyncRun> {
    const body: Record<string, unknown> = {};
    if (req.tenants !== undefined) body.tenants = req.tenants;
    if (req.includeMainDb !== undefined) body.includeMainDb = req.includeMainDb;
    // Il backend defaulta a dry-run se il flag non è esplicitamente false:
    // va sempre passato quando si vuole un sync reale.
    if (req.dryRun !== undefined) body.dryRun = req.dryRun;
    const response = (await this.stagingClient.post('/admin/staging/sync', body)) as {
      data: Record<string, unknown>;
    };
    return this.parseStagingSyncRun(response.data ?? {});
  }

  async getStagingSyncRuns(limit = 20): Promise<StagingSyncRun[]> {
    const response = (await this.stagingClient.get(`/admin/staging/sync-runs?limit=${limit}`)) as {
      data: Record<string, unknown>[];
    };
    const data = Array.isArray(response.data) ? response.data : [];
    return data.map((r) => this.parseStagingSyncRun(r));
  }

  async getStagingTenantSummary(
    tenantId: string,
    days = 7,
  ): Promise<StatsSummary & { totalChatMessages?: number }> {
    const response = (await this.stagingClient.get(
      `/admin/tenants/${tenantId}/logs/summary?days=${days}`,
    )) as { data: Record<string, unknown> };
    const data = response.data ?? {};
    const chatMessages =
      data.totalChatMessages ?? data.totalMessages ?? data.chatMessages;
    return {
      totalRequests: Number(data.totalRequests ?? data.requests ?? 0),
      totalErrors: Number(data.totalErrors ?? data.errors ?? 0),
      totalConversations: Number(data.totalConversations ?? data.conversations ?? 0),
      averageResponseTime: Number(data.averageResponseTime ?? 0),
      averageChatbotResponseTime: Number(data.averageChatbotResponseTime ?? 0),
      totalChatMessages: chatMessages !== undefined ? Number(chatMessages) : undefined,
    };
  }

  private parseStagingSyncRun(e: Record<string, unknown>): StagingSyncRun {
    const stats = Array.isArray(e.stats) ? e.stats : [];
    return {
      _id: String(e._id ?? e.id ?? ''),
      startedAt: String(e.startedAt ?? ''),
      finishedAt: e.finishedAt ? String(e.finishedAt) : null,
      dryRun: Boolean(e.dryRun ?? false),
      includeMainDb: Boolean(e.includeMainDb ?? false),
      requestedTenants: Array.isArray(e.requestedTenants)
        ? (e.requestedTenants as unknown[]).filter((v): v is string => typeof v === 'string')
        : [],
      stats: stats.map((s) => {
        const stat = s as Record<string, unknown>;
        return {
          from: String(stat.from ?? ''),
          to: String(stat.to ?? ''),
          collections: Number(stat.collections ?? 0),
          docs: Number(stat.docs ?? 0),
        };
      }),
      status: (e.status as StagingSyncRun['status']) ?? 'failed',
      error: e.error ? String(e.error) : null,
    };
  }
}

export const apiService = new ApiService();

function toWeeklyReportConfig(raw: unknown): WeeklyReportConfig {
  const data = (raw ?? {}) as Record<string, unknown>;
  return {
    enabled: data.enabled === true,
    cronExpression: typeof data.cronExpression === 'string' && data.cronExpression ? data.cronExpression : '0 8 * * 1',
    recipients: Array.isArray(data.recipients) ? data.recipients.map(String) : [],
  };
}
